/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {FeaturesApplyInsertService} from '../../../../services/features-apply-insert.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {NgEventBus} from 'ng-event-bus';
import {LineService} from '../../../../services/features/line.service';
import {LookupService} from '../../../../../common/service/lookup.service';
import {PlanModelService} from '../../../../../common/service/plan-model.service';
import {MapToolsService} from '../../../../services/map-tools.service';
import {SpatialSequenceService} from '../../../../../common/service/spatial-sequence.service';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {first, tap} from 'rxjs';
import {PointTypeLookup} from '../../../../../common/models/point-type.model';
import {BoundaryOffsetConstructionToolService} from './boundary-offset-construction-tool.service';
import {forkJoinDefaultIfEmpty} from '../../../../../common/observable.utils';

@Injectable({
    providedIn: 'root'
})
export class CornerOffsetConstructionToolService {
    title = 'CornerOffsetConstructionToolService';

    attributes: any;

    constructor(
        private boundaryOffsetConstructionToolService: BoundaryOffsetConstructionToolService,
        private constructionToolModelService: ConstructionToolModelService,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private lineService: LineService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private mapToolsService: MapToolsService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService
    ) {}

    create(attributes: any) {
        console.debug(`${this.title} - create`);
        return this.mapToolsService.activate('CONSTRUCTION_TOOLS_CORNER_OFFSET', () => {
            this.attributes = attributes;
            const selectedLines = this.spatialUtilService.getHighlightedLines();
            const lineProperties = this.validateSelection(selectedLines, attributes);
            if (lineProperties && lineProperties['valid']) {
                const subscription = this.createCornerOffset(attributes, lineProperties).pipe(first()).subscribe();
                this.eventBus.cast('nlr:busy', subscription);
            }
        });
    }

    private createCornerOffset(attributes: any, lineProperties: any) {
        const boundaryDistAttribute1 = attributes['l1boundarydistance'];
        const boundaryDistAttribute2 = attributes['l2boundarydistance'];
        const offsetDistanceAttribute1 = attributes['l1offsetdistance'];
        const offsetDistanceAttribute2 = attributes['l2offsetdistance'];

        let offsetDistance1 = attributes['l1offsetdistance'];
        let boundaryDist1 = lineProperties['boundaryDist1'];
        let boundaryDist2 = lineProperties['boundaryDist2'];

        const scaleFactor = parseFloat(attributes['scalefactor']);
        if (!isNaN(scaleFactor) && scaleFactor > 0) {
            offsetDistance1 = offsetDistance1 * scaleFactor;
            boundaryDist1 = boundaryDist1 * scaleFactor;
            boundaryDist2 = boundaryDist2 * scaleFactor;
        }

        const lineIds = this.spatialSequenceService.getConstructLineSequence(4);
        const pointIds = this.spatialSequenceService.getConstructPointSequence(3);

        const offsetBoundaryPoint1 = this.boundaryOffsetConstructionToolService.getPointAlongLine(lineProperties['line1Bearing'], boundaryDist1);
        const offsetBoundaryPoint2 = this.boundaryOffsetConstructionToolService.getPointAlongLine(lineProperties['line2Bearing'], boundaryDist2);

        // create the 2 boundary points
        const pointsToBeCreated = this.createBoundaryPoints(lineProperties, lineIds, pointIds, offsetBoundaryPoint1, offsetBoundaryPoint2);

        // create the corner point
        this.createCornerPoint(lineProperties, pointsToBeCreated, lineIds, pointIds, offsetDistance1, offsetBoundaryPoint1, attributes);

        // create the lines
        const linesToBeCreated = this.createLines(
            lineProperties,
            pointsToBeCreated,
            boundaryDistAttribute1,
            boundaryDistAttribute2,
            offsetDistanceAttribute1,
            offsetDistanceAttribute2,
            lineIds
        );

        return forkJoinDefaultIfEmpty([
            // commit the line features to the database
            this.createLineFeatures(linesToBeCreated).pipe(
                tap((createdLines: any[]) => console.debug(`${this.title} - created line count: ${createdLines.length}`))
            ),
            // commit the point features to the database
            this.createPointFeatures(pointsToBeCreated).pipe(
                tap((createdPoints: any[]) => console.debug(`${this.title} - created point count: ${createdPoints.length}`))
            )
        ]);
    }

    private createLines(
        lineProperties: any,
        pointsToBeCreated: any[],
        boundaryDistAttribute1: any,
        boundaryDistAttribute2: any,
        offsetDistanceAttribute1: any,
        offsetDistanceAttribute2: any,
        lineIds: any[]
    ) {
        const linesToBeCreated = [];
        const cornerConstructTypeLookup = this.boundaryOffsetConstructionToolService.getConstructTypeCornerLookup();
        const constructTypeId = cornerConstructTypeLookup.id;

        const lineTypeLookup = this.boundaryOffsetConstructionToolService.getLineTypeLookup();
        const lineType = lineTypeLookup.id;

        // create the first boundary line attributes
        const lineAttributes1 = {
            frompointid: `[${lineProperties.line1FromPt.attributes.id}]`,
            topointid: `(${pointsToBeCreated[0].attributes.id})`,
            type: lineType,
            constructtype: constructTypeId,
            distance: boundaryDistAttribute1,
            bearing: lineProperties.line1Bearing,
            id: lineIds[0],
            guid: this.planModelService.getGuid()
        };

        let fromPt = lineProperties.line1FromPt;
        let toPt = pointsToBeCreated[0];

        // create the first boundary line
        const firstBoundaryLine = this.createLine(lineAttributes1, fromPt, toPt);
        linesToBeCreated.push(firstBoundaryLine);

        // create the 2nd boundary line attributes
        const lineAttributes2 = {
            frompointid: `[${lineProperties.line2FromPt.attributes.id}]`,
            topointid: `(${pointsToBeCreated[1].attributes.id})`,
            type: lineType,
            constructtype: constructTypeId,
            distance: boundaryDistAttribute2,
            bearing: lineProperties.line2Bearing,
            id: lineIds[2],
            guid: this.planModelService.getGuid()
        };

        fromPt = lineProperties.line2FromPt;
        toPt = pointsToBeCreated[1];

        // create the 2nd  boundary line
        const secondBoundaryLine = this.createLine(lineAttributes2, fromPt, toPt);
        linesToBeCreated.push(secondBoundaryLine);

        // create the first offset line attributes
        const lineAttributes3 = {
            frompointid: `(${pointsToBeCreated[0].attributes.id})`,
            topointid: `(${pointsToBeCreated[2].attributes.id})`,
            type: lineType,
            constructtype: constructTypeId,
            distance: offsetDistanceAttribute1,
            bearing: lineProperties.offset1Bearing,
            id: lineIds[1],
            guid: this.planModelService.getGuid()
        };

        fromPt = pointsToBeCreated[0];
        toPt = pointsToBeCreated[2];

        // create the first offset line
        const firstOffsetLine = this.createLine(lineAttributes3, fromPt, toPt);
        linesToBeCreated.push(firstOffsetLine);

        // create the 2nd offset line attributes
        const lineAttributes4 = {
            frompointid: `(${pointsToBeCreated[1].attributes.id})`,
            topointid: `(${pointsToBeCreated[2].attributes.id})`,
            type: lineType,
            constructtype: constructTypeId,
            distance: offsetDistanceAttribute2,
            bearing: lineProperties.offset2Bearing,
            id: lineIds[3],
            guid: this.planModelService.getGuid()
        };

        fromPt = pointsToBeCreated[1];
        toPt = pointsToBeCreated[2];

        // create the 2nd offset line
        const secondOffsetLine = this.createLine(lineAttributes4, fromPt, toPt);
        linesToBeCreated.push(secondOffsetLine);

        return linesToBeCreated;
    }

    private createLine(lineAttributes: any, fromPoint: any, toPoint: any) {
        const lineGeometry = new this.esriModuleService.geometry.Polyline([
            [fromPoint.geometry.x, fromPoint.geometry.y],
            [toPoint.geometry.x, toPoint.geometry.y]
        ]);
        lineGeometry.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

        const lineGraphic = new this.esriModuleService.Graphic(lineGeometry);
        lineGraphic.attributes = lineAttributes;

        return lineGraphic;
    }

    private createCornerPoint(
        lineProperties: any,
        pointsToBeCreated: any[],
        lineIds: any[],
        pointIds: any[],
        offsetDistance1: number,
        offsetBoundaryPoint1: any[],
        attributes: any
    ) {
        const sourceLineIdsString = lineIds.map((lineId: any) => `[${lineId}]`).join();

        const cornerPointAttributes = {
            sourcefromlineid: lineProperties.line1.attributes.id,
            sourcetolineid: lineProperties.line2.attributes.id,
            constructfromlineid: lineIds[1],
            constructtolineid: lineIds[3],
            sourcelineids: sourceLineIdsString,
            id: pointIds[2]
        };

        this.getCornerPointAttributes(attributes, cornerPointAttributes);

        const offsetLine1Bearing = lineProperties['offset1Bearing'];
        const cornerPointCoordinates = this.boundaryOffsetConstructionToolService.getPointAlongLine(offsetLine1Bearing, offsetDistance1);

        const boundaryPoint1x = lineProperties.line1FromPt.geometry.x + offsetBoundaryPoint1[0];
        const boundaryPoint1y = lineProperties.line1FromPt.geometry.y + offsetBoundaryPoint1[1];

        const x = boundaryPoint1x + cornerPointCoordinates[0];
        const y = boundaryPoint1y + cornerPointCoordinates[1];
        const pointGraphic = this.createPointGraphic(cornerPointAttributes, x, y);

        pointsToBeCreated.push(pointGraphic);

        return pointsToBeCreated;
    }

    private getCornerPointAttributes(attributes: any, pointAttributes: any) {
        const cornerConstructTypeLookup = this.boundaryOffsetConstructionToolService.getConstructTypeCornerLookup();
        const pointTypeLookup = this.boundaryOffsetConstructionToolService.getPointTypeLookup();

        pointAttributes.type = pointTypeLookup.id;
        pointAttributes.constructtype = cornerConstructTypeLookup.id;
        pointAttributes.guid = this.planModelService.getGuid();

        for (const [key, value] of Object.entries(attributes) as any[]) {
            if (value && typeof value === 'object') {
                pointAttributes[key] = value.id;
            } else {
                pointAttributes[key] = value;
            }
        }
    }

    private createBoundaryPoints(lineProperties: any, lineIds: any[], pointIds: any[], offsetBoundaryPoint1: any[], offsetBoundaryPoint2: any[]) {
        const pointsToBeCreated = [];

        const sourceLineIdsString = lineIds.map((lineId: any) => `[${lineId}]`).join();

        // create the first boundary point
        const pointAttributes1 = this.getBoundaryPointAttributes({
            sourcefromlineid: lineProperties.line1.attributes.id,
            constructtolineid: lineIds[0],
            constructfromlineid: lineIds[1],
            sourcelineids: sourceLineIdsString,
            id: pointIds[0]
        });
        const firstBoundaryPoint = this.createPointGraphic(
            pointAttributes1,
            lineProperties.line1FromPt.geometry.x + offsetBoundaryPoint1[0],
            lineProperties.line1FromPt.geometry.y + offsetBoundaryPoint1[1]
        );
        pointsToBeCreated.push(firstBoundaryPoint);

        // create the 2nd boundary point
        const pointAttributes2 = this.getBoundaryPointAttributes({
            sourcefromlineid: lineProperties.line2.attributes.id,
            constructtolineid: lineIds[2],
            constructfromlineid: lineIds[3],
            sourcelineids: sourceLineIdsString,
            id: pointIds[1]
        });
        const secondBoundaryPoint = this.createPointGraphic(
            pointAttributes2,
            lineProperties.line2FromPt.geometry.x + offsetBoundaryPoint2[0],
            lineProperties.line2FromPt.geometry.y + offsetBoundaryPoint2[1]
        );
        pointsToBeCreated.push(secondBoundaryPoint);

        return pointsToBeCreated;
    }

    private createPointFeatures(points: any[]) {
        console.debug(`${this.title} - createPointFeatures`);

        return this.featuresApplyInsertService
            .insertFeaturesByType(this.spatialUtilService.getConstructPointMapLayer(), points)
            .pipe(tap(() => console.debug('Points have been successfully created!')));
    }

    private createLineFeatures(lines: any[]) {
        console.debug(`${this.title} - createLinesFeatures`);

        return this.featuresApplyInsertService
            .insertFeaturesByType(this.spatialUtilService.getConstructLineMapLayer(), lines)
            .pipe(tap(() => console.debug('Lines have been successfully created!')));
    }

    private createPointGraphic(attributes: any, x: number, y: number) {
        const pointGeometry = new this.esriModuleService.geometry.Point(x, y);
        pointGeometry.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

        const pointGraphic = new this.esriModuleService.Graphic(pointGeometry);
        pointGraphic.attributes = attributes;

        return pointGraphic;
    }

    private getBoundaryPointAttributes(attributes: any) {
        const cornerConstructTypeLookup = this.boundaryOffsetConstructionToolService.getConstructTypeCornerLookup();
        const pointTypeLookup = this.boundaryOffsetConstructionToolService.getPointTypeLookup();

        const pointAttributes: any = {
            type: pointTypeLookup.id,
            constructtype: cornerConstructTypeLookup.id,
            constructfromlineid: attributes.constructfromlineid,
            sourcefromlineid: attributes.sourcefromlineid,
            sourcelineids: attributes.sourcelineids,
            constructtolineid: attributes.constructtolineid,
            sourcetolineid: 0,
            id: attributes.id,
            guid: this.planModelService.getGuid()
        };

        for (const [key, value] of Object.entries(attributes) as any[]) {
            if (value && typeof value === 'object') {
                pointAttributes[key] = value.id;
            } else {
                pointAttributes[key] = value;
            }
        }

        const offsetConstructPointTypeLookup = this.getOffsetConstructPointLookup();
        pointAttributes.marktype = offsetConstructPointTypeLookup.id;

        return pointAttributes;
    }

    private getOffsetConstructPointLookup() {
        return this.lookupService.getLookups('pointType').find((l: PointTypeLookup) => l.description === 'Offset Construct Point');
    }

    private getBoundaryDistances(lineProperties: any, offsetDistance1: number, offsetDistance2: number) {
        const angleD = lineProperties['line2Bearing'] - lineProperties['line1Bearing'];
        const angleC = 360 - (180 + angleD);
        const a = offsetDistance1;
        const b = offsetDistance2;

        const c = Math.pow(a, 2) + Math.pow(b, 2) - 2 * (a * b) * Math.cos(angleC * (Math.PI / 180));
        const diagonal = Math.sqrt(c);

        // get angles where diagonal meets boundary lines for the lower triangle using sin law
        const sin_angle_cb = (Math.sin(angleC * (Math.PI / 180)) / diagonal) * a;
        const angle_cb = Math.asin(sin_angle_cb) * (180 / Math.PI);

        const sin_angle_ca = (Math.sin(angleC * (Math.PI / 180)) / diagonal) * b;
        const angle_ca = Math.asin(sin_angle_ca) * (180 / Math.PI);

        // subtract the angles from 90 degrees to get the angles for the upper triangle
        const angle_cd = 90 - angle_cb;
        const angle_ce = 90 - angle_ca;

        // now use sin rule to find the offset distances
        lineProperties['boundaryDist2'] = Math.abs(diagonal / Math.sin(angleD * (Math.PI / 180))) * Math.sin(angle_ce * (Math.PI / 180));
        lineProperties['boundaryDist1'] = Math.abs(diagonal / Math.sin(angleD * (Math.PI / 180))) * Math.sin(angle_cd * (Math.PI / 180));
        lineProperties['offsetDistance1'] = offsetDistance1;
        lineProperties['offsetDistance2'] = offsetDistance2;

        return lineProperties;
    }

    private getTool() {
        return this.constructionToolModelService.getTool('cornerOffset');
    }

    private validateSelection(selectedLineFeatures: any[], attributes: any) {
        const tool = this.getTool();

        tool.errors.length = 0;
        tool.warnings.length = 0;

        if (selectedLineFeatures.length === 2) {
            const line1 = selectedLineFeatures[0];
            const line2 = selectedLineFeatures[1];

            const pointIds = [line1.attributes.frompointid, line1.attributes.topointid, line2.attributes.frompointid, line2.attributes.topointid];

            const points = this.spatialUtilService.getPoints().filter((f: any) => pointIds.includes(f.attributes.id));

            const controlPoints = this.spatialUtilService.getControlPoints().filter((g: any) => pointIds.includes(g.attributes.id));

            const pointMap = {};
            points.forEach(
                (p: any) =>
                    (pointMap[p.attributes.id] = {
                        geometry: this.spatialUtilService.copyGeometry(p.geometry),
                        attributes: structuredClone(p.attributes)
                    })
            );

            controlPoints.forEach(
                (cp: any) =>
                    (pointMap[cp.attributes.id] = {
                        geometry: this.spatialUtilService.copyGeometry(cp.geometry),
                        attributes: structuredClone(cp.attributes)
                    })
            );

            let lineProperties = this.getLineProperties(line1, line2, attributes, pointMap);
            if (lineProperties) {
                if (lineProperties['line1Bearing'] > lineProperties['line2Bearing']) {
                    lineProperties = this.getLineProperties(line2, line1, attributes, pointMap);

                    if (lineProperties['line2Bearing'] - lineProperties['line1Bearing'] > 180) {
                        lineProperties = this.getLineProperties(line1, line2, attributes, pointMap);
                        this.validateOffsetAngles(lineProperties);
                    } else {
                        this.validateOffsetAngles(lineProperties);
                    }
                } else if (lineProperties['line2Bearing'] - lineProperties['line1Bearing'] > 180) {
                    lineProperties = this.getLineProperties(line2, line1, attributes, pointMap);
                    this.validateOffsetAngles(lineProperties);
                } else {
                    this.validateOffsetAngles(lineProperties);
                }
                return lineProperties;
            } else {
                tool.errors.push(tool.errorMessage2);
                return undefined;
            }
        } else {
            tool.errors.push(tool.errorMessage1);
            return undefined;
        }
    }

    private validateOffsetAngles(lineProperties: any) {
        const boundaryDist1 = lineProperties['boundaryDist1'];
        const boundaryDist2 = lineProperties['boundaryDist2'];
        const offsetDistance1 = lineProperties['offsetDistance1'];
        const offsetDistance2 = lineProperties['offsetDistance2'];

        const diagonal = Math.sqrt(Math.pow(boundaryDist1, 2) + Math.pow(offsetDistance1, 2));

        // get Angle to offset 1 triangle
        const a = offsetDistance1;
        let b = boundaryDist1;
        let c = diagonal;

        const b2c2_a2 = Math.pow(b, 2) + Math.pow(c, 2) - Math.pow(a, 2);
        const bc2 = 2 * (b * c);
        const cosQ = b2c2_a2 / bc2;
        const angleToTriangle1 = (Math.acos(cosQ) * 180) / Math.PI;

        let angleBetweenBearings = lineProperties['line2Bearing'] - lineProperties['line1Bearing'];
        if (Math.abs(angleBetweenBearings) > 180) {
            angleBetweenBearings = 360 - Math.abs(angleBetweenBearings);
        }

        const angleA = angleBetweenBearings - angleToTriangle1;
        b = diagonal;
        c = boundaryDist2;

        const a2 = Math.pow(b, 2) + Math.pow(c, 2) - 2 * (b * c) * Math.cos(angleA * (Math.PI / 180));
        const offsetDist2Calc = Math.sqrt(a2);
        const validDistance = Math.abs(offsetDist2Calc - offsetDistance2);
        if (validDistance > 0.01) {
            lineProperties['valid'] = false;
            const tool = this.getTool();
            tool.errors.push(tool.errorMessage3);
            return false;
        } else {
            lineProperties['valid'] = true;
            return true;
        }
    }

    private getLineProperties(line1: any, line2: any, attributes: any, pointMap: any) {
        let lineProperties = this.getLineStartAndEndPointIds(line1, line2);
        if (lineProperties) {
            lineProperties['line1'] = line1;
            lineProperties['line2'] = line2;

            lineProperties['line1FromPt'] = pointMap[lineProperties.line1FromPtId];
            lineProperties['line1ToPt'] = pointMap[lineProperties.line1ToPtId];
            lineProperties['line2FromPt'] = pointMap[lineProperties.line2FromPtId];
            lineProperties['line2ToPt'] = pointMap[lineProperties.line2ToPtId];

            lineProperties = this.getOffsetLineBearingProperties(lineProperties);

            const offsetDistance1 = attributes['l1offsetdistance'];
            const offsetDistance2 = attributes['l2offsetdistance'];

            lineProperties = this.getBoundaryDistances(lineProperties, offsetDistance1, offsetDistance2);
            return lineProperties;
        }
        return undefined;
    }

    private getOffsetLineBearingProperties(lineProperties: any) {
        let fromPtX = lineProperties['line1FromPt'].geometry.x;
        let fromPtY = lineProperties['line1FromPt'].geometry.y;
        let toPtX = lineProperties['line1ToPt'].geometry.x;
        let toPtY = lineProperties['line1ToPt'].geometry.y;
        lineProperties['line1Bearing'] = this.lineService.calculateBearing(fromPtX, fromPtY, toPtX, toPtY);

        fromPtX = lineProperties['line2FromPt'].geometry.x;
        fromPtY = lineProperties['line2FromPt'].geometry.y;
        toPtX = lineProperties['line2ToPt'].geometry.x;
        toPtY = lineProperties['line2ToPt'].geometry.y;
        lineProperties['line2Bearing'] = this.lineService.calculateBearing(fromPtX, fromPtY, toPtX, toPtY);

        if (lineProperties['line2Bearing'] - lineProperties['line1Bearing'] > 180) {
            lineProperties['offset1Bearing'] = lineProperties['line1Bearing'] - 90;
            lineProperties['offset2Bearing'] = lineProperties['line2Bearing'] + 90;
        } else {
            lineProperties['offset1Bearing'] = lineProperties['line1Bearing'] + 90;
            lineProperties['offset2Bearing'] = lineProperties['line2Bearing'] - 90;
        }

        if (lineProperties['offset1Bearing'] > 360) {
            lineProperties['offset1Bearing'] = lineProperties['offset1Bearing'] - 360;
        }
        if (lineProperties['offset2Bearing'] > 360) {
            lineProperties['offset2Bearing'] = lineProperties['offset2Bearing'] - 360;
        }
        if (lineProperties['offset1Bearing'] < 0) {
            lineProperties['offset1Bearing'] = 360 - Math.abs(lineProperties['offset1Bearing']);
        }
        if (lineProperties['offset2Bearing'] < 0) {
            lineProperties['offset2Bearing'] = 360 - Math.abs(lineProperties['offset2Bearing']);
        }
        return lineProperties;
    }

    private getLineStartAndEndPointIds(line1: any, line2: any) {
        if (line1.attributes.frompointid === line2.attributes.frompointid) {
            return {
                line1FromPtId: line1.attributes.frompointid,
                line1ToPtId: line1.attributes.topointid,
                line2FromPtId: line2.attributes.frompointid,
                line2ToPtId: line2.attributes.topointid
            };
        } else if (line1.attributes.frompointid === line2.attributes.topointid) {
            return {
                line1FromPtId: line1.attributes.frompointid,
                line1ToPtId: line1.attributes.topointid,
                line2FromPtId: line2.attributes.topointid,
                line2ToPtId: line2.attributes.frompointid
            };
        } else if (line1.attributes.topointid === line2.attributes.topointid) {
            return {
                line1FromPtId: line1.attributes.topointid,
                line1ToPtId: line1.attributes.frompointid,
                line2FromPtId: line2.attributes.topointid,
                line2ToPtId: line2.attributes.frompointid
            };
        } else if (line1.attributes.topointid === line2.attributes.frompointid) {
            return {
                line1FromPtId: line1.attributes.topointid,
                line1ToPtId: line1.attributes.frompointid,
                line2FromPtId: line2.attributes.frompointid,
                line2ToPtId: line2.attributes.topointid
            };
        }
        return undefined;
    }
}
